import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { NgClass, NgStyle } from '@angular/common';
import { SearchResultNavigator } from './search-result-navigator';
import { OcrSearchResultToWordRectPipe } from './ocr-search-result-to-word-rect.pipe';
import { fromEvent } from 'rxjs';
import { Dimension2d } from '../preview/page-overlay/model';
import { ScaleRectPipe } from '../preview/page-overlay/overlay-rect/scale-rect.pipe';
import { OverlayDimensionService } from '../preview/page-overlay/overlay-dimension.service';
import { RectDisplayStylePipe } from '../preview/page-overlay/overlay-rect/rect-display-style.pipe';
import { WaResizeObserver } from '@ng-web-apis/resize-observer';

// A smooth scroll animates through every page in between, which keeps intersection observers
// and image loading busy over the whole distance. Beyond a few screens that costs seconds.
const SMOOTH_SCROLL_MAX_SCREENS = 3;

const scrollBehaviourFor = (target: HTMLElement): ScrollBehavior =>
  Math.abs(target.getBoundingClientRect().top) > SMOOTH_SCROLL_MAX_SCREENS * window.innerHeight
    ? 'instant'
    : 'smooth';

@Component({
  selector: 'ct-shared-search-result-overlay',
  template: `
    <div
      (waResizeObserver)="updateDimensions()"
      class="pointer-events-none absolute top-0 right-0 bottom-0 left-0"></div>

    @let _dimension = dimension();
    @if (_dimension) {
      @let ping = pingedWord();
      @for (word of foundWords(); track $index) {
        @if (word !== ping) {
          <div
            #fieldHighlight
            class="bg-theme-blue/40 absolute"
            [ngStyle]="word | toWordRect | scaleRect: _dimension | rectDisplayStyle"></div>
        }
      }
      <div
        #pingElement
        [ngClass]="{ hidden: ping === undefined || ping.pageId !== pageId() }"
        #fieldHighlight
        class="absolute bg-purple-700/50"
        [ngStyle]="ping | toWordRect | scaleRect: _dimension | rectDisplayStyle"></div>
    }
  `,
  styles: `
    @keyframes ping {
      0% {
        transform: scale(0.5);
      }
      60% {
        transform: scale(1.5);
      }
      80% {
        transform: scale(1);
      }
    }

    .ping-box {
      animation: 0.2s 2 ease-out ping;
    }
  `,
  imports: [
    ScaleRectPipe,
    NgStyle,
    OcrSearchResultToWordRectPipe,
    NgClass,
    ScaleRectPipe,
    RectDisplayStylePipe,
    WaResizeObserver,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchResultOverlayComponent {
  readonly pageId = input.required<string>();

  protected readonly pingedWord = computed(() => this.searchResultNavigator.pingedWord());

  protected readonly foundWords = computed(
    () => this.searchResultNavigator.foundWords()[this.pageId()],
  );

  protected readonly dimension = computed<Dimension2d | undefined>(
    () => this.dimensionService.dimensionsByPageId()[this.pageId()],
  );

  private readonly searchResultNavigator = inject(SearchResultNavigator);
  private readonly dimensionService = inject(OverlayDimensionService);

  private readonly pingElement = viewChild<ElementRef<HTMLElement>>('pingElement');

  private readonly hostElement = inject(ElementRef);

  constructor() {
    effect(() => {
      const ping = this.pingElement();
      if (ping === undefined) return;
      fromEvent(ping.nativeElement, 'animationend', () =>
        ping.nativeElement.classList.remove('ping-box'),
      ).subscribe();
    });

    effect(() => {
      const ping = this.pingElement();
      const pingedWord = this.pingedWord();

      if (!ping || pingedWord?.pageId !== this.pageId()) return;

      ping.nativeElement.classList.remove('ping-box');
      // force a browser reflow which in turn forces the animation to restart
      void ping.nativeElement.offsetWidth;
      ping.nativeElement.classList.add('ping-box');

      setTimeout(() =>
        ping.nativeElement.scrollIntoView({
          block: 'center',
          behavior: scrollBehaviourFor(ping.nativeElement),
        }),
      );
    });
  }

  protected updateDimensions(): void {
    this.dimensionService.setDimension(this.pageId(), {
      width: this.hostElement.nativeElement.offsetWidth,
      height: this.hostElement.nativeElement.offsetHeight,
    });
  }
}
